import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AppRole } from '@/types/database';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: AppRole | null;
  isOwner: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isHeadAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; isAdmin?: boolean }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserRole = useCallback(async (userId: string): Promise<AppRole | null> => {
    try {
      // Order by role to prioritize admin > editor > viewer > user
      // Since 'admin' comes before 'user' alphabetically, ascending order works
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .order('role', { ascending: true })
        .limit(1);

      if (error) {
        console.error('Error fetching user role:', error);
        return null;
      }

      // Return the first (highest priority) role
      return data && data.length > 0 ? (data[0].role as AppRole) : null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    // Check if user opted out of "Remember Me" - if so, sign out on new browser session
    const checkSessionOnlyMode = async () => {
      const sessionOnly = sessionStorage.getItem('vsu_session_only');
      const wasSessionOnly = localStorage.getItem('vsu_was_session_only');
      
      // If the page is freshly loaded and we had a session-only flag before
      // but now it's gone (browser was closed and reopened), sign out
      if (!sessionOnly && wasSessionOnly === 'true') {
        localStorage.removeItem('vsu_was_session_only');
        await supabase.auth.signOut();
        return true;
      }
      
      // Store the session-only preference in localStorage to persist across sessions
      if (sessionOnly === 'true') {
        localStorage.setItem('vsu_was_session_only', 'true');
      }
      
      return false;
    };

    const initAuth = async () => {
      const shouldSignOut = await checkSessionOnlyMode();
      if (shouldSignOut) {
        setIsLoading(false);
        return;
      }

      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            setTimeout(() => {
              fetchUserRole(session.user.id).then(setUserRole);
            }, 0);
          } else {
            setUserRole(null);
          }

          setIsLoading(false);
        }
      );

      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          fetchUserRole(session.user.id).then(setUserRole);
        }

        setIsLoading(false);
      });

      return () => subscription.unsubscribe();
    };

    initAuth();
  }, [fetchUserRole]);

  const signIn = async (email: string, password: string): Promise<{ error: Error | null; isAdmin?: boolean }> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (error) {
      return { error, isAdmin: false };
    }

    // Fetch user role to check if admin
    if (data.user) {
      const role = await fetchUserRole(data.user.id);
      setUserRole(role);
      return { error: null, isAdmin: role === 'admin' || role === 'editor' };
    }

    return { error: null, isAdmin: false };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Clear all auth-related data and session flags
    sessionStorage.removeItem('vsu_session_only');
    localStorage.removeItem('vsu_was_session_only');
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUserRole(null);
  };

  // Owner is the super admin with all powers
  const isOwner = userRole === 'owner';
  // Admin includes owner role
  const isAdmin = userRole === 'admin' || userRole === 'owner';
  // Editor includes admin and owner
  const isEditor = userRole === 'editor' || userRole === 'admin' || userRole === 'owner';
  // Head admin is now the owner - only owner can manage roles
  const isHeadAdmin = userRole === 'owner';

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userRole,
        isOwner,
        isAdmin,
        isEditor,
        isHeadAdmin,
        isLoading,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
